import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  ChevronDown,
  Send,
  Phone,
  Search,
  FlaskConical,
  Droplet,
  Microscope,
  Camera,
  TestTube,
  CheckCircle2,
  Clock,
  MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useWhatsApp } from '../context/WhatsAppContext';
import api from '../utils/api';

const CATEGORIAS = [
  {
    id: 'quimica',
    titulo: 'Química / Perfiles',
    descripcion: 'Paneles bioquímicos y perfiles por sistema',
    icono: FlaskConical,
  },
  {
    id: 'hematologia',
    titulo: 'Hematología',
    descripcion: 'Biometría hemática y análisis de sangre',
    icono: Droplet,
  },
  {
    id: 'copro',
    titulo: 'Copro / Microscopía',
    descripcion: 'Análisis coproparasitoscópicos y microscópicos',
    icono: Microscope,
  },
];

const EstudiosSimple = () => {
  const { toast } = useToast();
  const { setWhatsappMessage, resetWhatsappMessage, setWhatsappEstudio } = useWhatsApp();
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    nombreLaboratorio: '',
    ubicacion: '',
    estudioSolicitado: '',
    observaciones: ''
  });
  const [loading, setLoading] = useState(false);
  const [estudios, setEstudios] = useState({ quimica: [], hematologia: [], copro: [] });
  const [loadingEstudios, setLoadingEstudios] = useState(true);
  const [expandedEstudio, setExpandedEstudio] = useState(null);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('quimica');

  useEffect(() => {
    const loadEstudios = async () => {
      try {
        setLoadingEstudios(true);
        const response = await api.get('/estudios-catalogo/');
        setEstudios(response.data.estudios);
      } catch (error) {
        console.error('Error loading estudios:', error);
      } finally {
        setLoadingEstudios(false);
      }
    };
    loadEstudios();
  }, []);

  // Mensaje por defecto al entrar a /estudios
  useEffect(() => {
    setWhatsappMessage('Hola, me gustaría solicitar un estudio veterinario.');
    return () => resetWhatsappMessage();
  }, [setWhatsappMessage, resetWhatsappMessage]);

  // Actualizar mensaje del FAB según el estudio expandido
  useEffect(() => {
    if (!expandedEstudio) {
      setWhatsappMessage('Hola, me gustaría solicitar un estudio veterinario.');
      setWhatsappEstudio(null);
      return;
    }
    const all = [...estudios.quimica, ...estudios.hematologia, ...estudios.copro];
    const found = all.find((e) => e.id === expandedEstudio);
    if (found) {
      setWhatsappMessage(
        `Hola, quiero solicitar el estudio: ${found.nombre} (${found.precio}). ¿Me pueden dar más información?`
      );
      setWhatsappEstudio(found.nombre);
    }
  }, [expandedEstudio, estudios, setWhatsappMessage, setWhatsappEstudio]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const applyFilter = (arr) =>
      !q
        ? arr
        : arr.filter(
            (e) =>
              e.nombre.toLowerCase().includes(q) ||
              (e.requisitos || []).some((r) => r.toLowerCase().includes(q))
          );
    return {
      quimica: applyFilter(estudios.quimica || []),
      hematologia: applyFilter(estudios.hematologia || []),
      copro: applyFilter(estudios.copro || []),
    };
  }, [estudios, search]);

  const counts = {
    quimica: filtered.quimica.length,
    hematologia: filtered.hematologia.length,
    copro: filtered.copro.length,
  };

  const toggleEstudio = (estudioId) => {
    setExpandedEstudio(expandedEstudio === estudioId ? null : estudioId);
  };

  const handleSolicitar = (nombreEstudio) => {
    setFormData((prev) => ({ ...prev, estudioSolicitado: nombreEstudio }));
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nombreLaboratorio || !formData.ubicacion || !formData.estudioSolicitado) {
      toast({
        title: 'Datos incompletos',
        description: 'Completa los campos obligatorios para enviar la solicitud.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      await api.post('/estudios/solicitar', formData);
      toast({
        title: 'Solicitud enviada',
        description: 'Nos pondremos en contacto contigo en las próximas horas.',
      });
      setFormData({ nombreLaboratorio: '', ubicacion: '', estudioSolicitado: '', observaciones: '' });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo enviar la solicitud. Intenta nuevamente.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const renderEstudioItem = (estudio) => {
    const isExpanded = expandedEstudio === estudio.id;
    return (
      <div
        key={estudio.id}
        className={`group border border-gray-200 rounded-xl bg-white transition-all duration-200 overflow-hidden ${
          isExpanded ? 'shadow-lg border-[#1e40af]/30' : 'hover:shadow-md hover:border-[#1e40af]/20'
        }`}
        data-testid={`estudio-card-${estudio.id}`}
      >
        <button
          onClick={() => toggleEstudio(estudio.id)}
          className="w-full flex items-center justify-between gap-4 p-5 text-left"
          data-testid={`estudio-toggle-${estudio.id}`}
        >
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className="hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#1e3a8a]/5 text-[#1e3a8a] group-hover:bg-[#1e3a8a] group-hover:text-white transition-colors">
              <TestTube className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900 text-base sm:text-[17px] leading-snug">
                {estudio.nombre}
              </h3>
              <p className="text-xs text-gray-500 mt-1 hidden sm:block">
                {(estudio.requisitos || []).slice(0, 2).join(' · ')}
                {estudio.requisitos && estudio.requisitos.length > 2 ? ' · …' : ''}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 sm:gap-5 shrink-0">
            <div className="text-right">
              <span className="block text-[11px] uppercase tracking-wider text-gray-400 font-medium">
                Desde
              </span>
              <span className="text-xl sm:text-2xl font-bold text-[#1e3a8a] tabular-nums">
                {estudio.precio}
              </span>
            </div>
            <ChevronDown
              className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                isExpanded ? 'rotate-180 text-[#1e40af]' : ''
              }`}
            />
          </div>
        </button>

        {isExpanded && (
          <div className="px-5 pb-5 pt-0 border-t border-gray-100 bg-gradient-to-b from-gray-50/50 to-white">
            <div className="pt-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#1e40af] mb-3">
                Requisitos de la muestra
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                {(estudio.requisitos || []).map((req, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-xs sm:text-sm text-gray-700"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 text-[#84cc16]" />
                    {req}
                  </span>
                ))}
              </div>

              {estudio.nota && (
                <div className="flex gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <span className="text-amber-600 text-sm font-semibold shrink-0">Nota:</span>
                  <span className="text-amber-900 text-sm">{estudio.nota}</span>
                </div>
              )}

              <Button
                onClick={() => handleSolicitar(estudio.nombre)}
                className="w-full sm:w-auto bg-[#1e40af] hover:bg-[#1e3a8a] text-white font-semibold"
                data-testid={`estudio-solicitar-${estudio.id}`}
              >
                <Send className="mr-2 h-4 w-4" />
                Solicitar este estudio
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSection = (cat) => {
    const Icon = cat.icono;
    const items = filtered[cat.id] || [];
    if (items.length === 0) return null;

    return (
      <section key={cat.id} id={`cat-${cat.id}`} className="mb-12 scroll-mt-28">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-lg bg-[#1e3a8a] text-white flex items-center justify-center shrink-0">
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#1e3a8a] leading-tight">
              {cat.titulo}
            </h2>
            <p className="text-sm text-gray-500">{cat.descripcion}</p>
          </div>
          <span className="ml-auto text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
            {items.length} {items.length === 1 ? 'estudio' : 'estudios'}
          </span>
        </div>

        <div className="grid gap-3">{items.map(renderEstudioItem)}</div>
      </section>
    );
  };

  const totalResultados = counts.quimica + counts.hematologia + counts.copro;

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="estudios-page">
      {/* HERO */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#1e3a8a] via-[#1e40af] to-[#2563eb] text-white">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.8) 1px, transparent 0)',
            backgroundSize: '24px 24px',
          }}
        />
        <div className="relative max-w-6xl mx-auto px-4 py-14 sm:py-20">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#bef264] bg-white/5 border border-white/10 rounded-full px-3 py-1 mb-5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#84cc16] animate-pulse" />
              Laboratorio Veterinario
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-4">
              Catálogo de Estudios
            </h1>
            <p className="text-base sm:text-lg text-blue-100 max-w-xl leading-relaxed">
              Resultados confiables para clínicas y hospitales veterinarios.
              Solicita tu estudio y nosotros coordinamos la recolección.
            </p>
          </div>

          <div className="mt-8 max-w-2xl">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar estudio o requisito…"
                className="pl-12 h-14 bg-white text-gray-900 border-0 shadow-xl rounded-xl text-base focus-visible:ring-2 focus-visible:ring-[#84cc16]"
                data-testid="estudios-search-input"
              />
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3 text-sm">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-4 py-2">
              <Camera className="h-4 w-4 text-[#bef264]" />
              <span>Evidencia fotográfica en microscopía</span>
            </div>
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-4 py-2">
              <TestTube className="h-4 w-4 text-[#bef264]" />
              <span>Reposición de tubos rojos y lilas</span>
            </div>
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-4 py-2">
              <Clock className="h-4 w-4 text-[#bef264]" />
              <span>Respuesta el mismo día</span>
            </div>
          </div>
        </div>
      </div>

      {/* CATEGORIES NAV (sticky) */}
      <div className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-2 overflow-x-auto py-3 -mx-4 px-4 scrollbar-hide">
            {CATEGORIAS.map((cat) => (
              <a
                key={cat.id}
                href={`#cat-${cat.id}`}
                onClick={() => setActiveTab(cat.id)}
                className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeTab === cat.id
                    ? 'bg-[#1e3a8a] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid={`categoria-tab-${cat.id}`}
              >
                <cat.icono className="h-4 w-4" />
                {cat.titulo}
                <span
                  className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${
                    activeTab === cat.id ? 'bg-white/20' : 'bg-white text-gray-600'
                  }`}
                >
                  {counts[cat.id] || 0}
                </span>
              </a>
            ))}
            <div className="ml-auto hidden sm:block text-xs text-gray-500 whitespace-nowrap">
              {totalResultados} {totalResultados === 1 ? 'resultado' : 'resultados'}
            </div>
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="max-w-6xl mx-auto px-4 py-10">
        {loadingEstudios ? (
          <div className="flex items-center justify-center py-24">
            <div className="text-center">
              <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-[#1e40af] border-t-transparent mb-4" />
              <p className="text-gray-600">Cargando catálogo…</p>
            </div>
          </div>
        ) : totalResultados === 0 ? (
          <div className="text-center py-20 bg-white border border-gray-200 rounded-2xl">
            <Search className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900">Sin resultados</h3>
            <p className="text-gray-500 mt-1">
              No encontramos estudios que coincidan con "{search}".
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => setSearch('')}
              data-testid="clear-search-button"
            >
              Limpiar búsqueda
            </Button>
          </div>
        ) : (
          <>{CATEGORIAS.map((cat) => renderSection(cat))}</>
        )}

        {/* FORMULARIO */}
        <section
          ref={formRef}
          id="formulario-solicitud"
          className="mt-8 bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm"
          data-testid="solicitud-form-section"
        >
          <div className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] text-white p-6 sm:p-8">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center">
                <Send className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold">Solicitar un estudio</h2>
                <p className="text-sm text-blue-100 mt-0.5">
                  Envía tus datos y te contactamos para coordinar la muestra.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-5" data-testid="solicitud-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Laboratorio / Veterinaria <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.nombreLaboratorio}
                  onChange={(e) => setFormData({ ...formData, nombreLaboratorio: e.target.value })}
                  placeholder="Clínica Veterinaria San Francisco"
                  required
                  data-testid="solicitud-laboratorio-input"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Ubicación <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.ubicacion}
                  onChange={(e) => setFormData({ ...formData, ubicacion: e.target.value })}
                  placeholder="Dirección o link de Google Maps"
                  required
                  data-testid="solicitud-ubicacion-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Estudio solicitado <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                value={formData.estudioSolicitado}
                onChange={(e) => setFormData({ ...formData, estudioSolicitado: e.target.value })}
                placeholder="Selecciona un estudio arriba o escríbelo aquí"
                required
                data-testid="solicitud-estudio-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Observaciones <span className="text-gray-400 font-normal">(opcional)</span>
              </label>
              <Textarea
                value={formData.observaciones}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                placeholder="Información adicional, urgencia, especie, etc."
                rows={3}
                data-testid="solicitud-observaciones-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#84cc16] hover:bg-[#65a30d] text-white font-semibold h-12 text-base"
              data-testid="solicitud-submit-button"
            >
              {loading ? (
                'Enviando solicitud…'
              ) : (
                <>
                  <Send className="mr-2 h-5 w-5" />
                  Enviar solicitud
                </>
              )}
            </Button>
          </form>
        </section>

        {/* WhatsApp */}
        <div className="mt-8 bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-5 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-green-500/10 flex items-center justify-center text-green-600">
              <MessageCircle className="h-7 w-7" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">¿Dudas sobre un estudio?</h3>
              <p className="text-sm text-gray-500">Contáctanos directamente por WhatsApp</p>
            </div>
          </div>
          <a
            href="https://wa.me/525545547772"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            data-testid="whatsapp-cta-button"
          >
            <Phone className="h-5 w-5" />
            55 4554 7772
          </a>
        </div>
      </div>
    </div>
  );
};

export default EstudiosSimple;
